import { useCallback, useRef, useEffect } from 'react';
import { isOpaqueCell } from '../dbCellTags';
import { cellToText } from '../dbCellText';
import { cellKey } from '../dbGridModel';
import { dateColumnKind } from '../dbGridCells';
import { isRevertToOriginal, LONG_VALUE_THRESHOLD } from './dbResultsTabHelpers';

/** Pending rows and in-grid cell editing: begin, type, commit, stage NULL / value, date picker, cell viewer. */
export function useDbCellEditing({
  result,
  isQueryTab,
  pendingRows,
  setPendingRows,
  pendingEdits,
  setPendingEdits,
  editingCell,
  setEditingCell,
  editingValue,
  setEditingValue,
  datePicker,
  setDatePicker,
  columnTypeMap,
  setSelectedRows,
  selectionAnchorRef,
  transposed,
  setTransposed,
  setCellViewer,
  scrollTopRef,
  setScrollRestore,
  pendingRowIdRef,
}) {
  const addPendingRow = useCallback(() => {
    if (!result) return;
    const id = ++pendingRowIdRef.current;
    setPendingRows((prev) => [...prev, { id, values: result.columns.map(() => '') }]);
  }, [result, setPendingRows, pendingRowIdRef]);

  const updatePendingCell = useCallback((rowId, colIndex, value) => {
    setPendingRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, values: r.values.map((v, i) => (i === colIndex ? value : v)) } : r))
    );
  }, [setPendingRows]);

  const removePendingRow = useCallback((rowId) => {
    setPendingRows((prev) => prev.filter((r) => r.id !== rowId));
  }, [setPendingRows]);

  const beginEditCell = useCallback((rowIndex, colIndex, cellValue) => {
    const key = `${rowIndex}:${colIndex}`;
    const pending = pendingEdits[key];
    const effective = pending !== undefined ? pending : cellValue;
    // What renderCellValue draws for these is a summary — a size, or the name
    // of a type we failed to read. Opening an editor seeded with that would
    // stage the summary itself as the cell's new contents. Set NULL stays
    // available; it is a real, unambiguous write.
    if (isOpaqueCell(effective)) return;
    setEditingCell({ rowIndex, colIndex });
    setEditingValue(cellToText(effective));
  }, [pendingEdits, setEditingCell, setEditingValue]);

  // Type-to-edit: the typed character REPLACES the value, as in Excel / DBeaver
  // (a long value does not detour through the viewer). Opaque cells refuse.
  const typeIntoCell = useCallback((rowIndex, colIndex, text) => {
    const key = `${rowIndex}:${colIndex}`;
    const effective = Object.prototype.hasOwnProperty.call(pendingEdits, key) ? pendingEdits[key] : result?.rows?.[rowIndex]?.[colIndex];
    if (effective === undefined || isOpaqueCell(effective)) return false;
    setEditingCell({ rowIndex, colIndex });
    setEditingValue(text);
    return true;
  }, [pendingEdits, result, setEditingCell, setEditingValue]);

  // The same for a NEW row, whose cells are their own inputs: a cell selected
  // by its edge (not its input) took no typing at all (owner, 07.10.2026 —
  // "unable to type in the NULL cell"). The key lands in that input, caret last.
  const typeIntoPendingCell = useCallback((rowId, colIndex, text) => {
    updatePendingCell(rowId, colIndex, text);
    requestAnimationFrame(() => {
      const input = document.querySelector(`[data-cell-key="${cellKey('p', rowId, colIndex)}"] input`);
      if (!input) return;
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    });
    return true;
  }, [updatePendingCell]);

  const cancelEditCell = useCallback(() => {
    setEditingCell(null);
    setEditingValue('');
  }, [setEditingCell, setEditingValue]);

  /**
   * Tab pressed while a cell editor has the caret: flip to/from the record
   * view and carry the edit across.
   *
   * The global Tab handler cannot do this. It bails on `editingCell`, and it
   * also bails whenever the event target is an input — which the editor always
   * is — so Tab fell through to the browser's own focus traversal and jumped
   * to whatever was tabbable after the table. That is the "it goes to the
   * bottom of the table" report.
   *
   * Three things are deliberately preserved that the non-editing Tab path does
   * not preserve: `editingCell` and `editingValue` (so the caret comes back in
   * the same cell with the same staged text) and `selectedCells` (so the cell
   * stays highlighted). Both grids key those by the same `rowIndex`/`colIndex`
   * into `result.rows`, so nothing needs translating between the two views.
   *
   * The row being edited is added to `selectedRows` on the way in, because the
   * record view draws one column per selected row — without it there would be
   * nothing to show the cell in.
   */
  /**
   * Hold the horizontal grid's scroll position across a record-view flip.
   *
   * `transposed` swaps which grid is mounted, so the horizontal one is
   * destroyed and rebuilt — a fresh mount whose viewport starts at zero. It
   * restores from `restoredScrollTop`, which is otherwise only written on tab
   * ARRIVAL, so the return trip out of the record view always landed at the
   * top however far down the user had been.
   *
   * `scrollTopRef` is the live position, and the transposed view never reports
   * scroll into it (it takes no `onScrollPosition`), so on the way back it
   * still describes the horizontal grid. Captured at flip time rather than
   * read at mount, because on a TAB switch the ref is still the previous tab's
   * value during the first render — the arrival effect has not run yet — and
   * restoring from it there would put the new tab at the old tab's offset.
   */
  const rememberScrollAcrossFlip = useCallback(() => {
    // Same tab, new position: the key must NOT change, or the horizontal grid
    // would treat the return trip as a different tab.
    setScrollRestore((prev) => ({ key: prev.key, top: scrollTopRef.current, anchor: true }));
  }, [scrollTopRef, setScrollRestore]);

  const toggleRecordViewWhileEditing = useCallback(() => {
    if (isQueryTab || !editingCell) return;
    flipKeepsEditRef.current = true;
    rememberScrollAcrossFlip();
    setTransposed((wasTransposed) => {
      if (!wasTransposed) {
        setSelectedRows((prev) => {
          if (prev.has(editingCell.rowIndex)) return prev;
          const next = new Set(prev);
          next.add(editingCell.rowIndex);
          return next;
        });
        if (selectionAnchorRef.current == null) selectionAnchorRef.current = editingCell.rowIndex;
      }
      return !wasTransposed;
    });
  }, [isQueryTab, editingCell, rememberScrollAcrossFlip, setTransposed, setSelectedRows, selectionAnchorRef]);

  // Marks the cell as changed locally (highlighted, shown with the new
  // value) without touching the database — the write only happens when the
  // user explicitly saves (Ctrl+S), same as pending new rows.
  //
  // NULL semantics: the inline editor stages exactly what was typed — an
  // empty string stages an empty string. NULL is only staged through the
  // explicit ∅ affordance (Alt+N / the button / the detail viewer), which
  // removes the old empty-string-means-NULL ambiguity. The one carve-out:
  // committing an untouched empty editor over a NULL cell stays NULL.
  /**
   * Set while a record-view flip is unmounting the cell editor.
   *
   * The editor commits on blur, and flipping the view destroys the input that
   * holds the caret — but losing the DOM node because the grid was rebuilt is
   * not the user leaving the cell, and committing there would end the edit the
   * flip is explicitly trying to carry across. Cleared by an effect on
   * `transposed`, i.e. once the other view has rendered its own editor.
   *
   * DEFENSIVE, AND NOT PROVEN BY THE SUITE — stated so nobody deletes it on the
   * strength of a green run, and nobody trusts it on one either. Removing this
   * line leaves `Tab while editing flips to the record view…` passing, because
   * jsdom does not fire blur for a focused element that is simply removed.
   * Real engines vary on that, and the failure it guards against is silent (the
   * edit ends, the caret vanishes, nothing errors), so the check stays until a
   * browser says it is unnecessary.
   */
  const flipKeepsEditRef = useRef(false);
  useEffect(() => {
    flipKeepsEditRef.current = false;
  }, [transposed]);

  const commitEditCell = useCallback(() => {
    // A blur caused by the record-view flip tearing the input down is not a
    // commit — see flipKeepsEditRef. The staged value and `editingCell` stay
    // exactly as they are, and the other view's editor picks them straight up.
    if (flipKeepsEditRef.current) return;
    if (!editingCell || !result) {
      setEditingCell(null);
      return;
    }
    const { rowIndex, colIndex } = editingCell;
    const originalValue = result.rows[rowIndex][colIndex];
    const newValue = editingValue;
    const key = `${rowIndex}:${colIndex}`;
    setEditingCell(null);
    const hasPending = Object.prototype.hasOwnProperty.call(pendingEdits, key);
    const effective = hasPending ? pendingEdits[key] : originalValue;
    // No-op relative to what the editor showed (NULL renders as an empty
    // editor, so an empty commit over NULL is "unchanged", not "empty string").
    if (effective === null ? newValue === '' : cellToText(effective) === newValue) return;
    // Typing the original text back reverts the staged edit entirely.
    if (originalValue !== null && cellToText(originalValue) === newValue) {
      setPendingEdits((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setPendingEdits((prev) => ({ ...prev, [key]: newValue }));
  }, [editingCell, editingValue, result, pendingEdits, setPendingEdits, setEditingCell]);

  // Stage an explicit NULL for a cell (∅ button, Alt+N, context menu, or the
  // detail viewer). Staging NULL over an already-NULL cell clears any pending
  // edit instead.
  const stageCellNull = useCallback((rowIndex, colIndex) => {
    if (!result) return;
    const key = `${rowIndex}:${colIndex}`;
    setEditingCell(null);
    setEditingValue('');
    const original = result.rows[rowIndex][colIndex];
    setPendingEdits((prev) => {
      if (original === null) {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: null };
    });
  }, [result, setPendingEdits, setEditingCell, setEditingValue]);

  // Detail-viewer save path: stages string-or-NULL exactly like inline edits.
  const stageCellValue = useCallback((rowIndex, colIndex, value) => {
    if (!result) return;
    if (value === null) {
      stageCellNull(rowIndex, colIndex);
      return;
    }
    const key = `${rowIndex}:${colIndex}`;
    const original = result.rows[rowIndex][colIndex];
    setPendingEdits((prev) => {
      if (isRevertToOriginal(original, value)) {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
  }, [result, setPendingEdits, stageCellNull]);

  // Opens the custom date/time picker for a temporal cell, anchored at the
  // clicked cell's rect. Seeds it with the cell's effective (pending-edit- or
  // pending-row-aware) value so reopening shows what's staged, not the stale
  // original. `target` is 'saved' (result row index) or 'pending' (temp id).
  const openDatePicker = useCallback((target, id, colIndex, rect) => {
    if (!result) return;
    const kind = dateColumnKind(columnTypeMap[result.columns[colIndex]]);
    if (!kind) return;
    let value;
    if (target === 'pending') {
      value = pendingRows.find((r) => r.id === id)?.values[colIndex] ?? '';
    } else {
      const key = `${id}:${colIndex}`;
      const eff = Object.prototype.hasOwnProperty.call(pendingEdits, key)
        ? pendingEdits[key]
        : result.rows[id]?.[colIndex];
      value = eff == null ? '' : cellToText(eff);
    }
    setEditingCell(null);
    setDatePicker({ kind, target, id, col: colIndex, value, rect });
  }, [result, columnTypeMap, pendingRows, pendingEdits, setEditingCell, setDatePicker]);

  const applyDatePick = useCallback((str) => {
    if (!datePicker) return;
    if (datePicker.target === 'pending') updatePendingCell(datePicker.id, datePicker.col, str);
    else stageCellValue(datePicker.id, datePicker.col, str);
    setDatePicker(null);
  }, [datePicker, updatePendingCell, stageCellValue, setDatePicker]);

  const clearDatePick = useCallback(() => {
    if (!datePicker) return;
    // A pending row carries NULL as the empty string (the input's placeholder
    // shows "NULL"); a saved row stages an explicit SQL NULL.
    if (datePicker.target === 'pending') updatePendingCell(datePicker.id, datePicker.col, '');
    else stageCellNull(datePicker.id, datePicker.col);
    setDatePicker(null);
  }, [datePicker, updatePendingCell, stageCellNull, setDatePicker]);

  // Opens the modal detail viewer for a cell, seeded with the effective
  // (pending-edit-aware) value. Read-only on query tabs.
  const openCellViewer = useCallback((rowIndex, colIndex, readOnly) => {
    if (!result) return;
    const key = `${rowIndex}:${colIndex}`;
    const hasPending = Object.prototype.hasOwnProperty.call(pendingEdits, key);
    const value = hasPending ? pendingEdits[key] : result.rows[rowIndex][colIndex];
    setEditingCell(null);
    setCellViewer({ rowIndex, colIndex, value, readOnly });
  }, [result, pendingEdits, setEditingCell, setCellViewer]);

  // Double-click dispatch: long/JSON values open the detail viewer (read-only
  // grids only ever open it read-only); everything else keeps the in-place
  // input on editable grids.
  const handleCellOpen = useCallback((rowIndex, colIndex, cellValue) => {
    const key = `${rowIndex}:${colIndex}`;
    const hasPending = Object.prototype.hasOwnProperty.call(pendingEdits, key);
    const effective = hasPending ? pendingEdits[key] : cellValue;
    const isLong =
      effective !== null &&
      effective !== undefined &&
      (typeof effective === 'object' || cellToText(effective).length >= LONG_VALUE_THRESHOLD);
    // Binary and undecodable cells open in the viewer, never the editor.
    if (isOpaqueCell(effective)) {
      openCellViewer(rowIndex, colIndex, true);
      return;
    }
    if (isQueryTab) {
      if (isLong) openCellViewer(rowIndex, colIndex, true);
      return;
    }
    if (isLong) openCellViewer(rowIndex, colIndex, false);
    else beginEditCell(rowIndex, colIndex, cellValue);
  }, [isQueryTab, pendingEdits, openCellViewer, beginEditCell]);

  return {
    addPendingRow,
    updatePendingCell,
    removePendingRow,
    typeIntoCell,
    typeIntoPendingCell,
    cancelEditCell,
    rememberScrollAcrossFlip,
    toggleRecordViewWhileEditing,
    commitEditCell,
    stageCellNull,
    stageCellValue,
    openDatePicker,
    applyDatePick,
    clearDatePick,
    openCellViewer,
    handleCellOpen,
  };
}
